import React, { useState } from 'react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { MapPin, Copy, Navigation, ExternalLink, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * AddressLink - Component that displays an address with options to copy or open in maps
 * 
 * @param {string} address - The address text to display
 * @param {string} className - Additional CSS classes
 * @param {boolean} showIcon - Whether to show the MapPin icon (default: true)
 * @param {string} iconClassName - CSS classes for the icon
 */
export function AddressLink({ 
  address, 
  className = '', 
  showIcon = true,
  iconClassName = 'w-4 h-4'
}) {
  const [copied, setCopied] = useState(false);

  if (!address) return null;

  const encodedAddress = encodeURIComponent(address);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      toast.success('Endereço copiado!');
      await new Promise(resolve => setTimeout(resolve, 2000));
      setCopied(false);
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = address;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      toast.success('Endereço copiado!');
      await new Promise(resolve => setTimeout(resolve, 2000));
      setCopied(false);
    }
  };

  const openGoogleMaps = () => {
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
  };

  const openWaze = () => {
    window.open(`https://waze.com/ul?q=${encodedAddress}&navigate=yes`, '_blank');
  };

  const openAppleMaps = () => {
    window.open(`https://maps.apple.com/?q=${encodedAddress}`, '_blank');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button 
          className={`inline-flex items-center gap-1.5 text-left hover:text-orange-600 dark:hover:text-orange-400 transition-colors cursor-pointer group ${className}`}
          data-testid="address-link"
        >
          {showIcon && (
            <MapPin className={`text-slate-400 group-hover:text-orange-500 transition-colors flex-shrink-0 ${iconClassName}`} />
          )}
          <span className="underline decoration-dotted decoration-slate-300 dark:decoration-slate-600 underline-offset-2 group-hover:decoration-orange-400">
            {address}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuItem onClick={handleCopy} className="cursor-pointer">
          {copied ? (
            <Check className="w-4 h-4 mr-2 text-green-500" />
          ) : (
            <Copy className="w-4 h-4 mr-2" />
          )}
          {copied ? 'Copiado!' : 'Copiar endereço'}
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem onClick={openGoogleMaps} className="cursor-pointer">
          <div className="w-4 h-4 mr-2 flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
            </svg>
          </div>
          Abrir no Google Maps
          <ExternalLink className="w-3 h-3 ml-auto text-slate-400" />
        </DropdownMenuItem>
        
        <DropdownMenuItem onClick={openWaze} className="cursor-pointer">
          <div className="w-4 h-4 mr-2 flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
              <path d="M20.54 6.63c-.68-1.35-1.67-2.49-2.87-3.32C16.47 2.47 15.12 2 13.59 2c-1.53 0-2.88.47-4.08 1.31-1.2.83-2.19 1.97-2.87 3.32-.68 1.35-1.04 2.85-1.04 4.47 0 .71.07 1.4.21 2.06.14.66.35 1.29.61 1.89.26.6.57 1.16.94 1.69.36.53.77 1.02 1.21 1.47.44.45.91.87 1.42 1.24.5.37 1.03.71 1.58 1 .55.29 1.12.54 1.71.74.59.2 1.19.36 1.8.47V24l4.37-4.37c1.09-.63 2.03-1.44 2.79-2.43.76-.99 1.35-2.12 1.75-3.38.4-1.26.6-2.62.6-4.09 0-1.62-.36-3.12-1.04-4.47v-.03zM10.5 11c-.83 0-1.5-.67-1.5-1.5S9.67 8 10.5 8s1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm6 0c-.83 0-1.5-.67-1.5-1.5S15.67 8 16.5 8s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
            </svg>
          </div>
          Abrir no Waze
          <ExternalLink className="w-3 h-3 ml-auto text-slate-400" />
        </DropdownMenuItem>

        <DropdownMenuItem onClick={openAppleMaps} className="cursor-pointer">
          <div className="w-4 h-4 mr-2 flex items-center justify-center">
            <Navigation className="w-4 h-4" />
          </div>
          Abrir no Apple Maps
          <ExternalLink className="w-3 h-3 ml-auto text-slate-400" />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default AddressLink;
